import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import brandMark from '../assets/img/org/logo-small.png';

const BRAND_COLOR = '#f47d44';
const BRAND_DARK = '#d95f22';
const INK = '#20283a';
const MUTED = '#6b7385';

export const DEFAULT_LOADER_DELAY = 1000; // ms

const useStyles = makeStyles(() => ({
    '@keyframes dpSpin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
    '@keyframes dpSpinBack': { from: { transform: 'rotate(360deg)' }, to: { transform: 'rotate(0deg)' } },
    '@keyframes dpBeat': {
        '0%, 100%': { transform: 'scale(1)', filter: 'drop-shadow(0 6px 14px rgba(244, 125, 68, 0.35))' },
        '50%': { transform: 'scale(0.86)', filter: 'drop-shadow(0 2px 6px rgba(244, 125, 68, 0.2))' },
    },
    '@keyframes dpFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
    '@keyframes dpSweep': { '0%': { transform: 'translateX(-110%)' }, '100%': { transform: 'translateX(320%)' } },
    '@keyframes dpDots': { '0%, 80%, 100%': { opacity: 0.2 }, '40%': { opacity: 1 } },

    wrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        width: '100%',
        animation: '$dpFadeIn .3s ease both',
    },
    fullScreen: {
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        backgroundColor: 'rgba(255, 255, 255, 0.82)',
        backdropFilter: 'blur(3px)',
    },
    ringBox: { position: 'relative', display: 'grid', placeItems: 'center' },
    ring: {
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        border: '3px solid transparent',
        borderTopColor: BRAND_COLOR,
        borderRightColor: BRAND_COLOR,
        animation: '$dpSpin 1.1s cubic-bezier(.6,.15,.4,.85) infinite',
    },
    ringSoft: {
        position: 'absolute',
        inset: 12,
        borderRadius: '50%',
        border: '2px dashed rgba(244, 125, 68, 0.35)',
        animation: '$dpSpinBack 3.4s linear infinite',
    },
    logo: { animation: '$dpBeat 1.4s ease-in-out infinite' },
    text: { fontSize: 16, fontWeight: 700, color: INK, letterSpacing: 0.2, textAlign: 'center', margin: 0 },
    subText: { fontSize: 13.5, fontWeight: 500, color: MUTED, textAlign: 'center', margin: '-12px 0 0' },
    dot: { animation: '$dpDots 1.2s infinite' },
    bar: {
        position: 'relative',
        width: 210,
        maxWidth: '70%',
        height: 5,
        borderRadius: 99,
        backgroundColor: '#eef0f5',
        overflow: 'hidden',
        '& > span': {
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: '35%',
            borderRadius: 99,
            background: 'linear-gradient(90deg, ' + BRAND_COLOR + ' 0%, ' + BRAND_DARK + ' 100%)',
            animation: '$dpSweep 1.15s ease-in-out infinite',
        },
    },
}));
export const useDecorpotLoader = (delay = DEFAULT_LOADER_DELAY, enabled = true) => {
    const [loading, setLoading] = useState(enabled && delay > 0);

    useEffect(() => {
        if (!enabled || delay <= 0) {
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        const timer = setTimeout(() => setLoading(false), delay);
        return () => clearTimeout(timer);
    }, [delay, enabled]);

    return loading;
};

const DecorpotLoader = ({
    text = 'Loading',
    subText = '',
    size = 124,
    fullScreen = false,
    minHeight = '60vh',
    showBar = true,
    showDots = true,
    className = '',
}) => {
    const classes = useStyles();
    const logoSize = Math.round(size * 0.42);

    return (
        <div
            className={classes.wrap + (fullScreen ? ' ' + classes.fullScreen : '') + (className ? ' ' + className : '')}
            style={fullScreen ? undefined : { minHeight }}
            role="status"
            aria-live="polite"
            aria-busy="true"
        >
            <div className={classes.ringBox} style={{ width: size, height: size }}>
                <span className={classes.ring} />
                <span className={classes.ringSoft} />
                <img src={brandMark} alt="Decorpot" className={classes.logo} style={{ width: logoSize, height: 'auto' }} />
            </div>

            <p className={classes.text}>
                {text}
                {showDots && (
                    <>
                        <span className={classes.dot}>.</span>
                        <span className={classes.dot} style={{ animationDelay: '.2s' }}>.</span>
                        <span className={classes.dot} style={{ animationDelay: '.4s' }}>.</span>
                    </>
                )}
            </p>

            {subText ? <p className={classes.subText}>{subText}</p> : null}

            {showBar && <div className={classes.bar}><span /></div>}
        </div>
    );
};

export default React.memo(DecorpotLoader);
